/**
 * Custom element for tabs functionality
 */
class TabsCustom extends HTMLElement {
  /** @type {HTMLElement | null} */
  get #header() {
    return this.querySelector('.tabs__header');
  }

  /** @type {HTMLButtonElement[]} */
  get #tabButtons() {
    return Array.from(this.querySelectorAll('.tabs__button'));
  }

  /** @type {HTMLElement[]} */
  get #tabPanels() {
    return Array.from(this.querySelectorAll('.tabs__panel'));
  }

  #controller = new AbortController();

  get #stickyMenu() {
    return this.hasAttribute('data-sticky-menu');
  }

  connectedCallback() {
    const { signal } = this.#controller;

    // Build tab buttons from panels if header is empty
    this.#buildTabButtons();

    if (this.#stickyMenu) {
      this.#setupStickyMenu({ signal });
    } else {
      // Initialize first tab as active if none is active
      this.#initializeActiveTab();
    }

    // Add click handlers to all tab buttons
    this.#tabButtons.forEach((button) => {
      button.addEventListener('click', this.#stickyMenu ? this.#handleStickyTabClick : this.#handleTabClick, { signal });
    });

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Builds tab buttons from tab panels if the header is empty
   */
  #buildTabButtons() {
    if (!this.#header) return;

    // Only build buttons if header is empty (no buttons exist)
    if (this.#tabButtons.length > 0) return;

    const panels = this.#tabPanels;
    if (panels.length === 0) return;

    let firstNonEmptyIndex = -1;
    panels.forEach((panel, index) => {
      // Skip empty tabs (marked in Liquid): no button, panel hidden via CSS
      if (panel.getAttribute('data-tab-empty') === 'true') return;

      if (firstNonEmptyIndex === -1) firstNonEmptyIndex = index;
      const isFirst = firstNonEmptyIndex === index;

      const heading = panel.dataset.heading || panel.getAttribute('data-heading') || `Tab ${index + 1}`;

      // Extract tab ID from panel ID or data attribute
      let tabId = panel.dataset.tabId;
      if (!tabId && panel.id) {
        tabId = panel.id.replace('tab-content-', '');
      }
      if (!tabId) {
        tabId = `tab-${index}`;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = `tabs__button${isFirst ? ' tabs__button--active' : ''}`;
      button.id = `tab-button-${tabId}`;
      button.dataset.tabId = tabId;
      button.setAttribute('aria-controls', panel.id || `tab-content-${tabId}`);
      button.setAttribute('aria-selected', isFirst ? 'true' : 'false');
      button.setAttribute('role', 'tab');
      button.textContent = heading;

      // Ensure panel has correct ID and data attribute
      if (!panel.id) {
        panel.id = `tab-content-${tabId}`;
      }
      if (!panel.dataset.tabId) {
        panel.dataset.tabId = tabId;
      }

      this.#header.appendChild(button);
    });
  }

  /**
   * Initializes the active tab if none is set
   */
  #initializeActiveTab() {
    const activeButton = this.querySelector('.tabs__button--active');
    if (!activeButton) {
      const firstButton = this.#tabButtons[0];
      if (firstButton) {
        const panelId = firstButton.getAttribute('aria-controls');
        const firstPanel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
        if (firstPanel) {
          this.#switchTab(firstButton, firstPanel);
        }
      }
    } else {
      // Ensure the corresponding panel is active
      const tabId = activeButton.dataset.tabId;
      const panel = this.querySelector(`#tab-content-${tabId}`);
      if (panel) {
        this.#setActivePanel(panel);
      }
    }
  }

  /**
   * Handles tab button clicks
   * @param {MouseEvent} event - The click event
   */
  #handleTabClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const tabId = button.dataset.tabId;
    const panel = this.querySelector(`#tab-content-${tabId}`);

    if (panel) {
      this.#switchTab(button, panel);
    }
  };

  /**
   * Handles tab button click in sticky menu mode: scroll to panel and set active
   * @param {MouseEvent} event - The click event
   */
  #handleStickyTabClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const tabId = button.dataset.tabId;
    const panel = this.querySelector(`#tab-content-${tabId}`);

    if (panel) {
      this.#setActiveButtonOnly(button);
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  /**
   * Sets only the button as active (for sticky mode; panels are all visible)
   * @param {HTMLButtonElement} button - The tab button to activate
   */
  #setActiveButtonOnly(button) {
    this.#tabButtons.forEach((btn) => {
      btn.classList.remove('tabs__button--active');
      btn.setAttribute('aria-selected', 'false');
    });
    button.classList.add('tabs__button--active');
    button.setAttribute('aria-selected', 'true');
  }

  /**
   * Sets up sticky menu: scroll spy and initial active tab
   * @param {{ signal: AbortSignal }} options
   */
  #setupStickyMenu({ signal }) {
    const panels = this.#tabPanels;
    const buttons = this.#tabButtons;
    if (panels.length === 0 || buttons.length === 0) return;

    // Sync with header: tabs go to top:0 as soon as header starts hiding (data-animating), not when it becomes idle (delay).
    const header = document.getElementById('header-component');
    const syncHeaderVisible = () => {
      if (!header) return;
      const state = header.getAttribute('data-sticky-state');
      const animating = header.hasAttribute('data-animating');
      const visible =
        (state === 'active' && !animating) || state === 'inactive';
      this.setAttribute('data-header-visible', visible ? 'true' : 'false');
    };
    if (header) {
      syncHeaderVisible();
      const observer = new MutationObserver(syncHeaderVisible);
      observer.observe(header, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'data-animating'],
      });
      signal?.addEventListener('abort', () => observer.disconnect());
    }

    // Set first tab as active initially
    this.#setActiveButtonOnly(buttons[0]);

    // Scroll spy: offset depends on whether site header is visible (same logic as syncHeaderVisible).
    const getStickyOffset = () => {
      const headerEl = document.getElementById('header-component');
      const state = headerEl?.getAttribute('data-sticky-state');
      const animating = headerEl?.hasAttribute('data-animating');
      const headerVisible =
        (state === 'active' && !animating) || state === 'inactive';
      if (!headerVisible) return 60;
      const headerHeightCss = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
      const headerPx = parseInt(headerHeightCss, 10) || 0;
      if (headerPx > 0) return headerPx + 60;
      const dataOffset = this.getAttribute('data-header-offset');
      return (dataOffset !== null && dataOffset !== '' ? parseInt(dataOffset, 10) || 120 : 120) + 60;
    };
    const checkActive = () => {
      const stickyOffset = getStickyOffset();
      let currentPanel = panels[0];
      for (const panel of panels) {
        const top = panel.getBoundingClientRect().top;
        if (top <= stickyOffset) currentPanel = panel;
      }
      const tabId = currentPanel.dataset.tabId || currentPanel.id?.replace('tab-content-', '');
      const button = this.querySelector(`#tab-button-${tabId}`);
      if (button && !button.classList.contains('tabs__button--active')) {
        this.#setActiveButtonOnly(button);
      }
    };

    window.addEventListener('scroll', checkActive, { passive: true, signal });
    checkActive();
  }

  /**
   * Switches to a new tab
   * @param {HTMLButtonElement} button - The tab button to activate
   * @param {HTMLElement} panel - The tab panel to show
   */
  #switchTab(button, panel) {
    // Remove active state from all buttons and panels
    this.#tabButtons.forEach((btn) => {
      btn.classList.remove('tabs__button--active');
      btn.setAttribute('aria-selected', 'false');
    });

    this.#tabPanels.forEach((pnl) => {
      pnl.classList.remove('tabs__panel--active');
    });

    // Add active state to selected button and panel
    button.classList.add('tabs__button--active');
    button.setAttribute('aria-selected', 'true');
    this.#setActivePanel(panel);
  }

  /**
   * Sets a panel as active
   * @param {HTMLElement} panel - The panel to activate
   */
  #setActivePanel(panel) {
    panel.classList.add('tabs__panel--active');
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeyDown = (event) => {
    const { key } = event;
    const activeButton = this.querySelector('.tabs__button--active');
    if (!activeButton) return;

    let targetButton = null;

    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        targetButton = this.#getPreviousButton(activeButton);
        break;
      case 'ArrowRight':
        event.preventDefault();
        targetButton = this.#getNextButton(activeButton);
        break;
      case 'Home':
        event.preventDefault();
        targetButton = this.#tabButtons[0];
        break;
      case 'End':
        event.preventDefault();
        targetButton = this.#tabButtons[this.#tabButtons.length - 1];
        break;
    }

    if (targetButton) {
      const tabId = targetButton.dataset.tabId;
      const panel = this.querySelector(`#tab-content-${tabId}`);
      if (panel) {
        this.#switchTab(targetButton, panel);
        targetButton.focus();
      }
    }
  };

  /**
   * Gets the previous tab button
   * @param {HTMLButtonElement} currentButton - The current button
   * @returns {HTMLButtonElement | null} The previous button
   */
  #getPreviousButton(currentButton) {
    const currentIndex = this.#tabButtons.indexOf(currentButton);
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : this.#tabButtons.length - 1;
    return this.#tabButtons[previousIndex] || null;
  }

  /**
   * Gets the next tab button
   * @param {HTMLButtonElement} currentButton - The current button
   * @returns {HTMLButtonElement | null} The next button
   */
  #getNextButton(currentButton) {
    const currentIndex = this.#tabButtons.indexOf(currentButton);
    const nextIndex = currentIndex < this.#tabButtons.length - 1 ? currentIndex + 1 : 0;
    return this.#tabButtons[nextIndex] || null;
  }
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}

